import axios from "axios";
import { BodyOf } from "../../core/api";
import { createMutationGroup } from "../../core/api/mutations";
import { auth } from "../../core/auth";
import { env } from "../../env";
import {
  ExternalPatientServicePlanId,
  PatientId,
  PatientServicePlanId,
  PatientServicePlanImportId,
} from "../../shared/schema/schema";
import { CoServicePlanScheduler } from "./components/CoServicePlanEngineTag";

export default createMutationGroup(({ api, createMutation }) => ({
  // Multipart upload — the typed client doesn't do FormData, so raw axios
  // like billing's UploadTab.
  uploadCalculator: (patientId: PatientId) =>
    createMutation({
      mutationFn: async (file: File) => {
        const authState = auth.getAuthOrFail();
        const data = new FormData();
        data.append("file", file, file.name);
        const response = await axios.post<{ importId: PatientServicePlanImportId }>(
          `${env.API_URL}agencies/${authState.data.agency.id}/agency_members/${authState.data.agencyMember.id}/patients/${patientId}/service_plan_imports`,
          data,
          { headers: { Authorization: `Token ${authState.tokens.authToken}` } }
        );
        return response.data;
      },
    }),
  retryImport: () =>
    createMutation({
      mutationFn: (importId: PatientServicePlanImportId) =>
        api.post("./service_plan_imports/:patientServicePlanImportId/retry", {
          path: { patientServicePlanImportId: importId },
        }),
    }),
  deleteServicePlan: () =>
    createMutation({
      mutationFn: (servicePlanId: PatientServicePlanId) =>
        api.delete("./service_plans/:patientServicePlanId", {
          path: { patientServicePlanId: servicePlanId },
        }),
    }),

  updateHeader: (servicePlanId: PatientServicePlanId) =>
    createMutation({
      mutationFn: (body: BodyOf<"put", "./service_plans/:patientServicePlanId">) =>
        api.put("./service_plans/:patientServicePlanId", {
          path: { patientServicePlanId: servicePlanId },
          body,
        }),
    }),

  replaceTasks: (servicePlanId: PatientServicePlanId) =>
    createMutation({
      mutationFn: (body: BodyOf<"put", "./service_plans/:patientServicePlanId/tasks">) =>
        api.put("./service_plans/:patientServicePlanId/tasks", {
          path: { patientServicePlanId: servicePlanId },
          body,
        }),
    }),

  upsertSignature: (servicePlanId: PatientServicePlanId) =>
    createMutation({
      mutationFn: (body: BodyOf<"post", "./service_plans/:patientServicePlanId/signatures">) =>
        api.post("./service_plans/:patientServicePlanId/signatures", {
          path: { patientServicePlanId: servicePlanId },
          body,
        }),
    }),

  // Emails a one-time signing link for one signature role. Re-sending revokes
  // whatever link was live before, so this doubles as "resend".
  sendSignatureClaim: (servicePlanId: PatientServicePlanId) =>
    createMutation({
      mutationFn: (
        body: BodyOf<"post", "./service_plans/:patientServicePlanId/signature_claims">
      ) =>
        api.post("./service_plans/:patientServicePlanId/signature_claims", {
          path: { patientServicePlanId: servicePlanId },
          body,
        }),
    }),

  finalize: (servicePlanId: PatientServicePlanId) =>
    createMutation({
      mutationFn: () =>
        api.post("./service_plans/:patientServicePlanId/finalize", {
          path: { patientServicePlanId: servicePlanId },
        }),
    }),

  // A coordinator's decision on a proposed CO service plan schedule. Approving is
  // the moment the patient's calendar actually changes — until then the import has
  // written nothing that governs care.
  approveCoImport: () =>
    createMutation({
      mutationFn: ({
        importId,
        startFrom,
      }: {
        importId: ExternalPatientServicePlanId;
        // When the approved schedule takes effect: today, or the first Sunday on or
        // after today. A current plan's own future start date wins either way.
        startFrom: "IMMEDIATELY" | "NEXT_SUNDAY";
      }) =>
        api.post("./co_service_plan_imports/:importId/approve", {
          path: { importId },
          body: { startFrom },
        }),
    }),

  declineCoImport: () =>
    createMutation({
      mutationFn: ({
        importId,
        notes,
      }: {
        importId: ExternalPatientServicePlanId;
        notes: string | null;
      }) =>
        api.post("./co_service_plan_imports/:importId/decline", {
          path: { importId },
          body: { notes },
        }),
    }),

  // Throw the proposal away and put the submission back on the queue, so another
  // schedule is built from the same plan. Nothing about the patient changes — a
  // proposal is all that is discarded.
  //
  // `scheduler` names which engine should build it, and sticks to the submission:
  // the worker's own retries use it too, so the week a coordinator eventually
  // approves is the one built by the engine they asked for.
  regenerateCoImport: () =>
    createMutation({
      mutationFn: ({
        importId,
        scheduler,
      }: {
        importId: ExternalPatientServicePlanId;
        // Null for a past plan, whose week the server always builds the same way and
        // refuses a scheduler for: the body then names none.
        scheduler: CoServicePlanScheduler | null;
      }) =>
        api.post("./co_service_plan_imports/:importId/regenerate", {
          path: { importId },
          body: scheduler === null ? {} : { scheduler },
        }),
    }),

  // The whole week, not a field of it. `acceptViolations` false asks the question
  // and writes nothing; true answers it and stages.
  replaceCoImportSchedule: () =>
    createMutation({
      mutationFn: ({
        importId,
        shifts,
        acceptViolations,
      }: {
        importId: ExternalPatientServicePlanId;
        shifts: BodyOf<"post", "./co_service_plan_imports/:importId/schedule">["shifts"];
        acceptViolations: boolean;
      }) =>
        api.post("./co_service_plan_imports/:importId/schedule", {
          path: { importId },
          body: { shifts, acceptViolations },
        }),
    }),

  // The PDF link is a time-limited signed URL, so it is fetched on demand rather
  // than cached as query data.
  getPdfUrl: () =>
    createMutation({
      mutationFn: (servicePlanId: PatientServicePlanId) =>
        api.get("./service_plans/:patientServicePlanId/pdf", {
          path: { patientServicePlanId: servicePlanId },
        }),
    }),
}));
